'use client'

import { IdiomMetadata } from '@/content/idioms'
import { useSettings } from '@/hooks/useSettings'
import { Link } from '@/i18n/routing'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BodyText } from '@/components/ui/typography'
import { Reveal } from '@/components/ui/reveal'
import { IconArrowRight } from '@tabler/icons-react'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'

type IdiomListClientProps = {
  idioms: IdiomMetadata[]
}

export function IdiomListClient({ idioms }: IdiomListClientProps) {
  const { settings } = useSettings()
  const t = useTranslations('idioms')

  const getDisplayScript = (idiom: IdiomMetadata['idiom']) => {
    return settings.scriptPriority === 'traditional' ? idiom.t : idiom.s
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {idioms.map((idiom, index) => (
        <Reveal
          key={idiom.slug}
          as="article"
          className="border-border/70 bg-card rounded-2xl border p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
          viewportAmount={0.4}
          delay={0.05 * index}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {/* Tag badge */}
          <div className="mb-4 flex items-center justify-between">
            <Badge variant="outline" size="sm">
              {idiom.tags?.[0] || 'General'}
            </Badge>
          </div>

          {/* Idiom title */}
          <h3
            className={cn(
              'mb-2 text-3xl font-bold tracking-tight',
              settings.scriptPriority === 'traditional'
                ? 'font-chinese-tc'
                : 'font-chinese-sc'
            )}
          >
            {getDisplayScript(idiom.idiom)}
          </h3>

          {/* Pronunciation */}
          <p className="text-muted-foreground mb-4 text-sm font-medium">
            {settings.pronunciationPriority === 'zhuyin'
              ? idiom.zhuyin
              : idiom.pinyin}
          </p>

          {/* Meaning preview */}
          <BodyText className="text-muted-foreground mb-6 line-clamp-3 text-sm">
            {idiom.meaning}
          </BodyText>

          {/* Read button */}
          <Button
            asChild
            variant="ghost"
            className="mt-auto w-full justify-start px-0"
          >
            <Link href={`/read/idioms/${idiom.slug}`}>
              {t('readIdiom')}
              <IconArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </Reveal>
      ))}
    </div>
  )
}
